import { ActivatedRouteSnapshot } from '@angular/router';

/**
 * ANALYTICS.25 -- what a click or page view is recorded as.
 *
 * Pure functions, kept apart from the listener so the rules that decide what
 * leaves the browser can be tested without a browser session. The server
 * re-applies the same rules (ui-event.service.ts); these are the first line,
 * not the boundary. Spec: .specify/specs/ANALYTICS.25-click-and-page-view-capture.md
 */

/** Anything a person can click to make something happen. */
export const INTERACTIVE_SELECTOR = [
  'a[href]',
  'button',
  'summary',
  'select',
  'input[type="checkbox"]',
  'input[type="radio"]',
  'input[type="submit"]',
  'input[type="button"]',
  'mat-option',
  '[role="button"]',
  '[role="link"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="option"]',
  '[role="switch"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="treeitem"]',
  '[data-track]'
].join(', ');

/**
 * Component wrappers that say nothing about WHICH feature was used. The area
 * of a click is the nearest cpd-* component that is not one of these, so a
 * button inside the Military-Friendly Employers panel is credited to that
 * panel and not to cpd-flat-button.
 */
export const GENERIC_AREAS = new Set([
  'cpd-root',
  'cpd-base-component',
  'cpd-flat-button',
  'cpd-stroked-button',
  'cpd-link-button',
  'cpd-copy-button',
  'cpd-dialog-container',
  'cpd-dialog-container2'
]);

/**
 * Route params whose VALUE is worth keeping. Everything else is recorded as
 * its pattern only: `:token` on share and return links is a credential,
 * `:slug` on a public page can be a name.
 */
export const SUBJECT_PARAMS = [
  'onetCode',
  'jobId',
  'pathwayId',
  'code'
] as const;

const EMAIL = /[^\s@]+@[^\s@]+\.[^\s@]+/g;
const FRAGMENT = /^[A-Za-z0-9_-]{1,40}$/;
const SUBJECT = /^[A-Za-z0-9._-]{1,64}$/;

export interface ClickDescription {
  area: string | null;
  target: string | null;
  element: string;
  href: string | null;
}

export interface RouteDescription {
  route: string;
  subject: string | null;
}

/** Whitespace collapsed, emails masked, cut to `max`. Empty becomes null. */
export function cleanLabel(
  value: string | null | undefined,
  max = 80
): string | null {
  if (!value) {
    return null;
  }
  const text = value.replace(EMAIL, '[email]').replace(/\s+/g, ' ').trim();
  return text ? text.slice(0, max) : null;
}

/** A fragment is kept only when it is a plain word, e.g. `resume`. */
export function cleanFragment(
  fragment: string | null | undefined
): string | null {
  return fragment && FRAGMENT.test(fragment) ? fragment : null;
}

/**
 * The matched route as a pattern, and the value of the one allow-listed param
 * it carries, if any. Walks the primary outlet only: that is the page.
 */
export function describeRoute(root: ActivatedRouteSnapshot): RouteDescription {
  const segments: string[] = [];
  let subject: string | null = null;
  let node: ActivatedRouteSnapshot | null = root;
  while (node) {
    const path = node.routeConfig?.path;
    if (path) {
      segments.push(path);
    }
    for (const name of SUBJECT_PARAMS) {
      const value = node.params?.[name];
      if (typeof value === 'string' && SUBJECT.test(value)) {
        subject = value;
      }
    }
    node = node.firstChild;
  }
  const route = ('/' + segments.join('/')).replace(/\/+/g, '/').slice(0, 200);
  return { route, subject };
}

/** What kind of control this is, in a word. */
function elementKind(el: Element): string {
  const role = el.getAttribute('role');
  if (role) {
    return role.slice(0, 32);
  }
  const tag = el.tagName.toLowerCase();
  if (tag === 'a') {
    return 'link';
  }
  if (tag === 'input') {
    return (el as HTMLInputElement).type || 'input';
  }
  if (tag === 'summary') {
    return 'disclosure';
  }
  if (tag === 'mat-option') {
    return 'option';
  }
  return tag.slice(0, 32);
}

/** The words a person would use to name the control they clicked. */
function visibleLabel(el: Element): string | null {
  const aria = el.getAttribute('aria-label');
  if (aria) {
    return aria;
  }
  if (el instanceof HTMLInputElement) {
    return el.labels?.[0]?.textContent ?? el.getAttribute('title');
  }
  if (el instanceof HTMLSelectElement) {
    return el.labels?.[0]?.textContent ?? el.getAttribute('title');
  }
  return el.textContent || el.getAttribute('title');
}

function areaOf(el: Element): string | null {
  const explicit = el
    .closest('[data-track-area]')
    ?.getAttribute('data-track-area');
  if (explicit) {
    return explicit.slice(0, 80);
  }
  let node: Element | null = el;
  while (node) {
    const tag = node.tagName.toLowerCase();
    if (tag.startsWith('cpd-') && !GENERIC_AREAS.has(tag)) {
      return tag;
    }
    node = node.parentElement;
  }
  return null;
}

/** External links only: origin and path. Internal ones show up as a view. */
function externalHref(el: Element, origin: string): string | null {
  if (!(el instanceof HTMLAnchorElement)) {
    return null;
  }
  try {
    const url = new URL(el.href);
    if (url.origin === origin) {
      return null;
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return null;
    }
    return `${url.origin}${url.pathname}`.slice(0, 300);
  } catch {
    return null;
  }
}

/**
 * The control a click landed on, or null when it did not land on one (a click
 * on plain text or empty space) or landed somewhere marked data-track-ignore.
 *
 * Nothing typed is ever read: text fields are not in INTERACTIVE_SELECTOR,
 * and a click inside an editable region is skipped outright.
 */
export function describeClick(
  target: EventTarget | null,
  origin: string = location.origin
): ClickDescription | null {
  if (!(target instanceof Element)) {
    return null;
  }
  const el = target.closest(INTERACTIVE_SELECTOR);
  if (!el) {
    return null;
  }
  if (el.closest('[data-track-ignore]')) {
    return null;
  }
  if (
    (el instanceof HTMLElement && el.isContentEditable) ||
    el.closest('textarea, [contenteditable="true"], [contenteditable=""]')
  ) {
    return null;
  }

  const named = el.getAttribute('data-track');
  const isPrivate = !!el.closest('[data-track-private]');
  return {
    area: areaOf(el),
    target: named
      ? cleanLabel(named, 120)
      : isPrivate
        ? null
        : cleanLabel(visibleLabel(el)),
    element: elementKind(el),
    href: externalHref(el, origin)
  };
}
